import React, { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import DelayInput from "../DelayInput";

import withTitle from "../withTitle";

import { V1, V2 } from "../../Api";
import { WellKnownGroups } from "../AuthContext";
import { formatName, formatDateLocal, truncateEllipse } from "../../Utilities";

interface IMarketingLeadsViewState {
    isLoading: boolean | string;

    searchPerson: string;
    searchText: string;
    searchOffset: number;

    leads: any[];
    marketers: any[];
}

const PAGE_SIZE = 50;

class MarketingLeadsView extends React.Component<RouteComponentPropsHistory<any, any>, IMarketingLeadsViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any>>) {
        super(props);

        const state: IMarketingLeadsViewState = {
            isLoading: true,

            searchPerson: "",
            searchText: "",
            searchOffset: 0,

            leads: [],
            marketers: []
        };

        if (props.location.state) {
            state.searchPerson = props.location.state.searchPerson;
            state.searchText = props.location.state.searchText;
            state.searchOffset = props.location.state.searchOffset;
        }

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.previousPage = this.previousPage.bind(this);
    }

    public async componentDidMount() {
        try {
            const marketers = await V1.get<any[]>(`employees/summary?group=${WellKnownGroups.Marketer}`);

            this.setState({
                marketers: marketers.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)))
            }, () => this.loadData());
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        return (
            <div className="viewer-root with-actionbar">
                <div className="filter-control-box">
                    <DelayInput type="search" className="fill-space" placeholder="Search..." timeout={500} name="searchText" value={this.state.searchText} onChangeDelayed={this.handleElementChange} />
                    <select value={this.state.searchPerson} name="searchPerson" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">Everyone</option>
                        <option value="caller">You</option>
                        {
                            this.state.marketers.map(a =>
                                <option key={a.id} value={a.id}>{formatName(a.name)}</option>
                            )
                        }
                    </select>
                </div>
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="col-smallest">Date</th>
                            <th>Name</th>
                            <th>Contact</th>
                            <th>Assigned To</th>
                            <th>Notes</th>
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Leads</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.isLoading === true
                            ? <tr><td colSpan={10}><div className="spinner spinner-center lds-dual-ring" /></td></tr>
                            : (typeof this.state.isLoading === "string")
                                ? <tr><td colSpan={10}><span className="error-text">{this.state.isLoading}</span></td></tr>
                                : this.state.leads.length === 0
                                    ? <tr><td colSpan={10}>No results found</td></tr>
                                    : this.state.leads.map(a => (
                                        <tr key={a.id}>
                                            <td data-caption="Date: ">{formatDateLocal(a.timestamp)}</td>
                                            <td>{a.name}</td>
                                            <td data-caption="Contact: ">{a.contactName || "N/A"}</td>
                                            <td>{a.assignedEmployee ? formatName(a.assignedEmployee.name) : ""}</td>
                                            <td className="wrap" data-caption="Notes: ">{truncateEllipse(a.lastNote || "", 40)}</td>
                                            <td><Link to={`/leads/${a.id}`} className="button smaller">View</Link></td>
                                        </tr>))
                        }
                    </tbody>
                </table>
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.leads.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        } as any, () => this.loadData());
    }

    private async loadData() {
        try {
            this.setState({
                isLoading: true,
            });

            this.saveSearchState();

            const parameters: string[] = [
                `limit=${PAGE_SIZE}`,
                `offset=${this.state.searchOffset}`
            ];
            if (this.state.searchText) {
                parameters.push(`q=${this.state.searchText}`);
            }
            if (this.state.searchPerson) {
                parameters.push(`person=${this.state.searchPerson}`);
            }

            const data = await V2.get<any[]>(`leads?${parameters.join("&")}`);

            this.setState({
                leads: data,
                isLoading: false,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load data"
            });
        }
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            searchOffset: this.state.searchOffset,
            searchPerson: this.state.searchPerson,
            searchText: this.state.searchText,
        };

        this.props.history.replace(location);
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.loadData());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.loadData());
    }
}

export default withTitle("Marketing Leads", MarketingLeadsView);